import { type FC } from "react";
import { AlertTriangle } from "lucide-react";
import { ArrowRight } from "lucide-react";

interface DocumentLimitBannerProps {
  documentCount: number;
  freeDocumentLimit: number;
  onUpgrade: () => void;
}

export const DocumentLimitBanner: FC<DocumentLimitBannerProps> = ({
  documentCount,
  freeDocumentLimit,
  onUpgrade,
}) => {
  const atLimit = documentCount >= freeDocumentLimit;
  const remaining = Math.max(0, freeDocumentLimit - documentCount);
  const progress = Math.min(100, (documentCount / freeDocumentLimit) * 100);

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-blue-500 bg-blue-50 p-4 mb-4 sm:flex-row sm:items-center">
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border border-primary bg-white">
          <AlertTriangle className="h-4 w-4 text-blue-500" />
        </div>

        <div className="flex flex-col gap-1 min-w-0">
          <p className="text-sm font-medium">
            {atLimit
              ? `You've reached your ${freeDocumentLimit}-document limit`
              : `${documentCount} of ${freeDocumentLimit} documents used`}
          </p>
          {atLimit ? (
            <p className="text-sm text-secondary">
              Upgrade to keep adding, or remove a document to free up a spot.
            </p>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-2 w-24 shrink-0 rounded-full bg-blue-200 overflow-hidden sm:w-[200px]">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="text-sm text-secondary truncate">
                {remaining} upload{remaining === 1 ? "" : "s"} left
              </p>
            </div>
          )}
        </div>
      </div>

      <button
        onClick={onUpgrade}
        className="flex shrink-0 items-center gap-1 text-sm font-medium text-primary hover:underline sm:ml-auto"
      >
        Add unlimited documents
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};
